import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Save,
  Cancel,
  ExpandMore,
  Person,
  Business,
  Phone,
  Email,
  LocationOn,
  Category,
  CalendarToday,
  Message,
  School,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { arSD } from '@mui/x-data-grid/locales';
import EditableField from '../components/CompanyDetails/EditableField';
import CompanyAuthUsers from '../components/CompanyDetails/CompanyAuthUsers';
import { supabase } from '../lib/supabase';
import { 
  getCompanyById, 
  getClassificationsByIds, 
  getCompanyMessages, 
  updateCompany 
} from '../lib/api';

interface Company {
  id: number;
  pcu_id: string;
  name: string;
  email: string;
  mobile_number?: string;
  landline_number?: string;
  fax_number?: string;
  city_id: number;
  classification_id?: any;
  classification_expire_date?: string;
  created_at: string;
  cities?: {
    city: string;
    translation: string;
    areas?: {
      area: string;
      translation: string;
    };
  };
  classifications?: Array<{
    id: number;
    category: string;
    field: string;
    specialization?: string;
  }>;
}

interface Message {
  id: number;
  title: string;
  content: string;
  created_at: string;
  is_read: boolean;
}

interface Training {
  id: number;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  location: string;
}

const CompanyDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Related data
  const [messages, setMessages] = useState<Message[]>([]);
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  // Dropdown options for edit mode
  const [cities, setCities] = useState<any[]>([]);
  const [allClassifications, setAllClassifications] = useState<any[]>([]);

  // Editable fields state
  const [editedData, setEditedData] = useState<Partial<Company>>({});

  useEffect(() => {
    if (id) {
      fetchCompanyDetails();
      fetchRelatedData();
      fetchDropdownOptions();
    }
  }, [id]);

  const fetchDropdownOptions = async () => {
    try {
      // Fetch cities with areas
      const { data: citiesData, error: citiesError } = await supabase
        .from('cities')
        .select(`
          id,
          city,
          translation,
          area_id,
          areas:area_id (
            id,
            area,
            translation
          )
        `)
        .order('city');
      
      if (!citiesError && citiesData) {
        setCities(citiesData);
      }

      // Fetch all classifications
      const { data: classificationsData, error: classificationsError } = await supabase
        .from('classifications')
        .select('id, category, field, specialization')
        .order('category');
      
      if (!classificationsError && classificationsData) {
        setAllClassifications(classificationsData);
      }
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
    }
  };

  const fetchCompanyDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use API instead of direct Supabase call
      const data = await getCompanyById(id!);

      // Fetch classifications
      if (data) {
        const classificationData = data.classifications || data.classification_id;
        let classIds: (number | string)[] = [];

        if (classificationData) {
          if (typeof classificationData === 'string') {
            try {
              const parsed = JSON.parse(classificationData);
              classIds = Array.isArray(parsed) ? parsed : [parsed];
            } catch (e) {
              // If parsing fails, it might be a single ID as a string
              const numId = parseInt(classificationData, 10);
              classIds = isNaN(numId) ? [] : [numId];
            }
          } else if (Array.isArray(classificationData)) {
            classIds = classificationData;
          } else if (typeof classificationData === 'number') {
            classIds = [classificationData];
          }

          // Filter out null, undefined, and empty strings - keep as strings (UUIDs)
          const validClassIds = classIds
            .filter((id) => id != null && id !== '')
            .map((id) => String(id)); // Convert to string to handle UUIDs

          if (validClassIds.length > 0) {
            // Use API to fetch classifications
            const classificationsData = await getClassificationsByIds(validClassIds);
            data.classifications = classificationsData || [];
          }
        }

        setCompany(data);
        setEditedData(data);
      }
    } catch (error: any) {
      console.error('Error fetching company:', error);
      setError(error.message || 'Failed to load company details');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedData = async () => {
    setLoadingRelated(true);
    try {
      // Use API to fetch messages for this company
      const messagesData = await getCompanyMessages(id!, 10);
      if (messagesData) setMessages(messagesData);

      // Note: Trainings table doesn't exist in database, so we set empty array
      setTrainings([]);
    } catch (error) {
      console.error('Error fetching related data:', error);
    } finally {
      setLoadingRelated(false);
    }
  };

  const handleSave = async () => {
    if (!company) return;

    setSaving(true);
    setError(null);
    try {
      // Prepare data for saving - map classification_id to classifications column
      const saveData = { ...editedData };
      if (saveData.classification_id) {
        (saveData as any).classifications = saveData.classification_id;
        delete saveData.classification_id;
      }
      
      // Use API to update company
      await updateCompany(company.id.toString(), saveData);
      
      // Refetch company details to get updated data with classifications
      await fetchCompanyDetails();
      setEditMode(false);
    } catch (error: any) {
      console.error('Error saving company:', error);
      setError(error.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset editedData with classification_id extracted from company
    const resetData = { ...company };
    if (company?.classifications) {
      (resetData as any).classification_id = company.classifications.map(c => c.id);
    }
    setEditedData(resetData);
    setEditMode(false);
    setError(null);
  };

  const handleEnterEditMode = () => {
    // Initialize editedData with classification_id from classifications
    const editData = { ...company };
    if (company?.classifications) {
      (editData as any).classification_id = company.classifications.map(c => c.id);
    }
    setEditedData(editData);
    setEditMode(true);
  };

  const handleFieldChange = (field: string, value: any) => {
    setEditedData({ ...editedData, [field]: value });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !company) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button sx={{ gap: 1 }} onClick={() => navigate('/companies')}>
          <ArrowBack sx={{ fontSize: 18 }} />{t('common.back')}
        </Button>
      </Box>
    );
  }

  if (!company) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('companies.notFound')}
        </Alert>
        <Button sx={{ gap: 1 }} onClick={() => navigate('/companies')}>
          <ArrowBack sx={{ fontSize: 18 }} />{t('common.back')}
        </Button>
      </Box>
    );
  }

  const messagesColumns: GridColDef[] = [
    { field: 'title', headerName: t('messages.title'), flex: 1 },
    { field: 'created_at', headerName: t('common.date'), width: 180, 
      valueFormatter: (value) => new Date(value).toLocaleDateString() },
    { field: 'is_read', headerName: t('messages.status'), width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value ? t('messages.read') : t('messages.unread')}
          color={params.value ? 'default' : 'primary'}
          size="small"
        />
      )
    },
  ];

  const trainingsColumns: GridColDef[] = [
    { field: 'title', headerName: t('trainings.title'), flex: 1 },
    { field: 'start_date', headerName: t('trainings.startDate'), width: 130,
      valueFormatter: (value) => new Date(value).toLocaleDateString() },
    { field: 'end_date', headerName: t('trainings.endDate'), width: 130,
      valueFormatter: (value) => new Date(value).toLocaleDateString() },
    { field: 'location', headerName: t('trainings.location'), width: 150 },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/companies')} size="large">
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              {company.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('companies.pcuId')}: {company.pcu_id}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {editMode ? (
            <>
              <Button
                variant="outlined"
                sx={{ gap: 1 }}
                onClick={handleCancel}
                disabled={saving}
              >
                <Cancel sx={{ fontSize: 18 }} />{t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                sx={{ gap: 1 }}
                onClick={handleSave}
                disabled={saving}
              >
                <Save sx={{ fontSize: 18 }} />{saving ? t('common.saving') : t('common.save')}
              </Button>
            </>
          ) : (
            <Button variant="contained" sx={{ gap: 1 }} onClick={handleEnterEditMode}>
              <Edit sx={{ fontSize: 18 }} />{t('common.edit')}
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main Information Card */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="h4" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business fontSize="small" /> {t('companies.basicInfo', 'Basic Information')}
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Row 1: Name and Email */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
                <EditableField
                  icon={<Business fontSize="small" />}
                  label={t('companies.name')}
                  value={editMode ? editedData.name : company.name}
                  editMode={editMode}
                  onChange={(value) => handleFieldChange('name', value)}
                  required
                />
              </Box>
              
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
                <EditableField
                  icon={<Email fontSize="small" />}
                  label={t('companies.email')}
                  value={editMode ? editedData.email : company.email}
                  editMode={editMode}
                  onChange={(value) => handleFieldChange('email', value)}
                  type="email"
                  required
                />
              </Box>
            </Box>

            {/* Row 2: Mobile and Landline */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
                <EditableField
                  icon={<Phone fontSize="small" />}
                  label={t('companies.mobileNumber')}
                  value={editMode ? editedData.mobile_number : company.mobile_number}
                  editMode={editMode}
                  onChange={(value) => handleFieldChange('mobile_number', value)}
                />
              </Box>

              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
                <EditableField
                  icon={<Phone fontSize="small" />}
                  label={t('companies.landlineNumber')}
                  value={editMode ? editedData.landline_number : company.landline_number}
                  editMode={editMode}
                  onChange={(value) => handleFieldChange('landline_number', value)}
                />
              </Box>
            </Box>

            {/* Row 3: Fax and City */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
                <EditableField
                  icon={<Phone fontSize="small" />}
                  label={t('companies.faxNumber')}
                  value={editMode ? editedData.fax_number : company.fax_number}
                  editMode={editMode}
                  onChange={(value) => handleFieldChange('fax_number', value)}
                />
              </Box>

              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
                <EditableField
                  icon={<LocationOn fontSize="small" />}
                  label={t('companies.city')}
                  value={editMode ? String(editedData.city_id || '') : String(company.city_id || '')}
                  displayValue={
                    isRTL
                      ? `${company.cities?.translation || ''} - ${company.cities?.areas?.translation || ''}`
                      : `${company.cities?.city || ''} - ${company.cities?.areas?.area || ''}`
                  }
                  editMode={editMode}
                  onChange={(value) => handleFieldChange('city_id', value)}
                  type="select"
                  options={cities.map(city => ({
                    value: city.id,
                    label: isRTL 
                      ? `${city.translation} - ${city.areas?.translation || ''}` 
                      : `${city.city} - ${city.areas?.area || ''}`
                  }))}
                />
              </Box>
            </Box>

            {/* Row 4: Classifications */}
            <Box>
              {editMode ? (
                <EditableField
                  icon={<Category fontSize="small" />}
                  label={t('companies.classifications')}
                  value={editedData.classification_id || []}
                  editMode={editMode}
                  onChange={(value) => handleFieldChange('classification_id', value)}
                  type="multiselect"
                  options={allClassifications.map(c => ({
                    value: c.id,
                    label: `${c.category} - ${c.field}${c.specialization ? ` (${c.specialization})` : ''}`
                  }))}
                />
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Category fontSize="small" color="action" />
                    <Typography variant="body1" color="text.secondary">
                      {t('companies.classifications')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {company.classifications && company.classifications.length > 0 ? (
                      company.classifications.map((c) => (
                        <Chip
                          key={c.id}
                          label={`${c.category} - ${c.field}${c.specialization ? ` (${c.specialization})` : ''}`}
                          variant="outlined"
                          color="primary"
                          size="small"
                        />
                      ))
                    ) : (
                      <Typography variant="body1" color="text.secondary">
                        {t('common.none', 'None')}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Box>

            {/* Row 5: Expiry Date */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
                <EditableField
                  icon={<CalendarToday fontSize="small" />}
                  label={t('companies.expiryDate')}
                  value={editMode ? editedData.classification_expire_date : company.classification_expire_date}
                  editMode={editMode}
                  onChange={(value) => handleFieldChange('classification_expire_date', value)}
                  type="date"
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Accordions for Related Data */}
      <Box sx={{ mb: 2 }}>
        {/* Auth Users */}
        <Accordion defaultExpanded sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person fontSize="small" />
              <Typography variant="h5">
                {t('companies.authUsers')}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1.5, pt: 0 }}>
            <CompanyAuthUsers 
              companyId={company?.id || id || ''} 
              companyEmail={company?.email}
              companyName={company?.name}
              editMode={editMode}
            />
          </AccordionDetails>
        </Accordion>

        {/* Messages */}
        <Accordion sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Message fontSize="small" />
              <Typography variant="h5">
                {t('companies.messages')}
              </Typography>
              {messages.length > 0 && (
                <Chip label={messages.length} size="small" color="primary" />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1.5, pt: 0 }}>
            {loadingRelated ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : messages.length > 0 ? (
              <Box sx={{ height: 350 }}>
                <DataGrid
                  rows={messages}
                  columns={messagesColumns}
                  pageSizeOptions={[5, 10]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                  }}
                  localeText={isRTL ? arSD.components.MuiDataGrid.defaultProps.localeText : undefined}
                  density="compact"
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                {t('messages.noMessages')}
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Trainings */}
        <Accordion sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <School fontSize="small" />
              <Typography variant="h5">
                {t('companies.trainings')}
              </Typography>
              {trainings.length > 0 && (
                <Chip label={trainings.length} size="small" color="primary" />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1.5, pt: 0 }}>
            {loadingRelated ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : trainings.length > 0 ? (
              <Box sx={{ height: 350 }}>
                <DataGrid
                  rows={trainings}
                  columns={trainingsColumns}
                  pageSizeOptions={[5, 10]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                  }}
                  localeText={isRTL ? arSD.components.MuiDataGrid.defaultProps.localeText : undefined}
                  density="compact"
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                {t('trainings.noTrainings')}
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default CompanyDetailsPage;
